import { useMemo, useState, useEffect, useContext, useRef } from 'react';

import { CheckCircleFilled, CloseOutlined } from '@ant-design/icons';
import { Modal, message, Select, Image } from 'antd';
import Cookies from 'js-cookie';

import { favoriteAdd } from '@/apis/member';
import {
  addCompareProduct,
  checkCompareProduct,
  getCompareProduct,
  removeAllCompareProduct,
  removeCompareProduct,
} from '@/apis/so';
import SvgIcon from '@/components/SvgIcon';
import { LayoutContext } from '@/layouts/baseLayout/provider';
import { ProductItemContext } from '@/providers/productItem';
import { ProductListContext } from '@/providers/productList';
import useProductStore from '@/store/product';
import { buriedPoint } from '@/utils/buriedPoint';
import Utils from '@/utils/Utils';

const defaultImage = 'https://static.szlcsc.com/ecp/assets/web/static/images/no_goods_pic_big.png';

// 比较和收藏按钮
export default function CompareAndCollect(props) {
  const { productId } = props;
  const { searchResult } = useContext(ProductListContext);
  const { index, productVO, rs_log, isFavorite } = useContext(ProductItemContext);
  const [isCollect, setIsCollect] = useState(false);
  const [isCompare, setIsCompare] = useState(false);
  const [hoverText, setHoverText] = useState('');
  const { updateCompareData, compareList } = useProductStore();
  const { resourceData } = useContext(LayoutContext);

  useEffect(() => {
    setIsCollect(isFavorite);
  }, [isFavorite]);

  useEffect(() => {
    setIsCompare(compareList?.includes(productId));
  }, [compareList, productId]);

  // 收藏
  async function collectClick() {
    buriedPoint({
      op: 'add_collect',
      data: {
        pc: productVO?.productId,
        brand: productVO?.productGradePlateName,
        model: productVO?.productModel,
        code: productVO?.productCode,
        kw: searchResult?.globalSearchKeyword,
        cp: searchResult?.currePage,
        ix: index,
        no: searchResult?.totalCount,
        rs_log,
      },
    });
    favoriteAdd({ productId }).then((res) => {
      if (res?.needLogin || res?.msg === 'needLogin') {
        Utils.showWindowLogin();
      } else if (res?.code === 200) {
        if (isCollect) {
          message.success('取消收藏成功！');
          setIsCollect(false);
        } else {
          setIsCollect(true);
          Modal.destroyAll();
          Modal.confirm({
            title: <span className="text-[16px] font-[500]">移入收藏夹成功</span>,
            icon: <CheckCircleFilled style={{ color: '#52C41A' }} />,
            closable: true,
            className: 'lc-cart-modal',
            centered: true,
            content: (
              <div className="text-[14px]">
                <p className="font-[400] text-[rgba(0,0,0,0.88)]">该商品已经成功添加到了您的收藏夹</p>
                <p>
                  <a
                    className="font-[400] text-[#1FA0E8]"
                    href={`${resourceData?.projectConfigVO.lcscMemberUrl}/member/favorite.html`}
                  >
                    查看我收藏的商品
                  </a>
                </p>
                <div className="mt-[20px] flex justify-end">
                  <button
                    className="ml-[15px] h-[32px] rounded-[4px] bg-[#0093E6] pl-[15px] pr-[15px] font-[400] text-[white] hover:opacity-[0.8]"
                    onClick={() => Modal.destroyAll()}
                  >
                    知道了
                  </button>
                </div>
              </div>
            ),
          });
        }
      }
    });
  }

  // 对比
  function compareClick() {
    updateCompareData({
      open: true,
      productVO: {
        ...productVO,
        key: new Date().getTime(),
      },
      rs_log,
      index,
    });
  }

  const collectIcon = useMemo(() => {
    if (isCollect || hoverText === '收藏') {
      return 'collect-hover';
    } else {
      return 'collect';
    }
  }, [isCollect, hoverText]);

  const CompareIcon = useMemo(() => {
    if (isCompare || hoverText === '对比') {
      return 'contrast-hover';
    } else {
      return 'contrast';
    }
  }, [isCompare, hoverText]);

  return (
    <>
      <ul className="flex">
        <li
          className="mr-[16px] flex cursor-pointer items-center justify-center text-[#333] hover:text-[#FAAD14]"
          onClick={collectClick}
          onMouseOver={() => setHoverText('收藏')}
          onMouseOut={() => setHoverText('')}
        >
          <SvgIcon app="so" iconName={collectIcon} className="mr-[2px] size-[17px] bg-cover"></SvgIcon>
          <span className={`${isCollect ? 'text-[#FAAD14]' : ''}`}>收藏</span>
        </li>
        <li
          className="mr-[16px] flex cursor-pointer items-center justify-center text-[#333] hover:text-[#FAAD14]"
          onClick={compareClick}
          onMouseOver={() => setHoverText('对比')}
          onMouseOut={() => setHoverText('')}
        >
          <SvgIcon app="so" iconName={CompareIcon} className="mr-[2px] size-[17px] bg-cover"></SvgIcon>
          <span>对比</span>
        </li>
      </ul>
    </>
  );
}
// 比较弹窗
export function CompareModal({ productVO, rs_log, index, open, onCancel, className }) {
  const [compareList, setCompareList] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const { resourceData } = useContext(LayoutContext);
  const { searchResult } = useContext(ProductListContext);
  const compareBtnRef = useRef();
  const { updateCompareList } = useProductStore();

  useEffect(() => {
    if (productVO?.productId && open) {
      addProduct();
    } else if (!open) {
      setIsOpen(false);
    }
  }, [open, productVO]);

  // 加入对比
  function addProduct() {
    addCompareProduct({ productId: productVO?.productId })
      .then(({ result }) => {
        switch (result?.check_result) {
          case 'existed':
            message.error('该商品已在对比栏');
            break;
          case 'fail':
            message.error('添加失败，对比栏已满');
            break;
          case 'success':
            message.success('已添加到对比栏');
            break;
          default:
        }
        getList();
      })
      .catch((error) => {
        console.error('对比异常', error);
        message.error(error?.msg);
      });
  }
  // 获取列表
  function getList() {
    const product_compare_bid = Cookies.get('product_compare_bid');
    const product_compare = Cookies.get('product_compare');
    if (!product_compare && product_compare_bid) {
      Cookies.set('product_compare_bid', '', {
        domain: '.szlcsc.com',
        path: '/',
      });
    }
    if (product_compare && !product_compare_bid) {
      Cookies.set('product_compare_bid', Utils.getUuid(), {
        domain: '.szlcsc.com',
        path: '/',
      });
    }
    buriedPoint({
      op: 'add_compare',
      data: {
        pc: productVO?.productId,
        brand: productVO?.productGradePlateName,
        model: productVO?.productModel,
        code: productVO?.productCode,
        kw: searchResult?.globalSearchKeyword,
        cp: searchResult?.currePage,
        ix: index,
        bi: Cookies.get('product_compare_bid'),
        no: searchResult?.totalCount,
        rs_log,
      },
    });
    getCompareProduct().then(({ result }) => {
      updateCompareList(result?.map((item) => item?.productId));
      setCompareList(result);
      if (!isOpen) {
        setIsOpen(true);
      }
    });
  }
  // 删除商品
  function clickDelCompare(productId) {
    removeCompareProduct({ productId }).then(() => {
      getList();
    });
  }
  // 删除所有商品
  function deleteAllProducts() {
    removeAllCompareProduct().then(() => {
      getList();
    });
  }
  // 对比商品
  function compareProduct() {
    if (compareList.length < 2) {
      message.error('至少选择两种商品进行对比！');
      return;
    }
    checkCompareProduct().then(({ result }) => {
      if (result.check_result === 'fail') {
        message.error('至少选择两种商品进行对比！');
      } else {
        const el = compareBtnRef.current;
        const url = Utils.getBtnSpmQueryUrl(
          `${resourceData?.projectConfigVO?.lcscSearchUrl}/compare/product/detail.html`,
          el
        );
        window.open(url, '_blank');
      }
    });
    // 用户行为分析埋点
    buriedPoint({
      op: 'compare',
      data: {
        pc: Cookies.get('product_compare'),
        bi: Cookies.get('product_compare_bid'),
      },
    });
  }

  const changePriceFn = (v, productPriceList) => {
    const priceList = [];
    const theRatio = parseInt(v.convesionRatio);
    productPriceList?.forEach((val) => {
      const spNumber = parseInt(val.startPurchasedNumber);
      const sepPrice = parseFloat(val.productPrice);
      const thePrice = parseFloat(sepPrice);
      const priceItem = {
        label: `${spNumber * theRatio}：￥${thePrice}`,
        value: spNumber,
      };
      priceList.push(priceItem);
    });
    return priceList;
  };

  const defaultItem = (idx) => (
    <>
      <div className="flex h-[82px] w-[280px]">
        <div className="h-[82px] w-[82px] rounded bg-[#f3f3f3] text-center">
          <b className="text-[50px] leading-[82px] text-[#ccc]">{idx + 1}</b>
        </div>
        <p className="ml-[10px] text-[14px] text-[#ccc]">你还可以继续添加</p>
      </div>
    </>
  );

  const productItem = (compareItem, idx) => {
    return (
      <>
        <div data-spm={`p${idx + 1}`} className="flex h-[82px] w-[280px] justify-start text-[12px] leading-[24px]">
          <a
            data-spm="t"
            href={`${resourceData?.projectConfigVO?.lcscItemUrl}/${compareItem?.productId}.html`}
            target="_blank"
            aria-label="点击查看详情"
          >
            <Image
              src={compareItem?.breviaryImageUrl || defaultImage}
              className="rounded"
              alt={compareItem?.title}
              height={82}
              width={82}
              preview={false}
            ></Image>
          </a>
          <div className="ml-[10px] w-[185px] pr-[20px]">
            <div className="lc-text-hidden flex justify-between">
              <a
                data-spm="n"
                href={`${resourceData?.projectConfigVO?.lcscItemUrl}/${compareItem?.productId}.html`}
                target="_blank"
                aria-label="点击查看详情"
              >
                <p className="lc-text-hidden w-[130px] hover:text-[#0093e6]">{compareItem?.productName}</p>
              </a>
              <button className="text-[#0093e6]" onClick={() => clickDelCompare(compareItem?.productId)}>
                删除
              </button>
            </div>
            <p className="lc-text-hidden">{compareItem?.productModel}</p>
            {compareItem?.isShowPrice === false ? (
              <>需询价</>
            ) : (
              <Select
                className="mt-[8px] w-[160px]"
                size="small"
                getPopupContainer={() => document.getElementById('compareArea')}
                defaultValue={
                  compareItem?.productPriceList[compareItem.productPriceList.length - 1]?.startPurchasedNumber
                }
                width={178}
                options={changePriceFn(compareItem, compareItem?.productPriceList)}
              />
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      {isOpen && (
        <div
          id="compareArea"
          title="对比栏"
          className={`fixed bottom-0 left-1/2 z-[99] w-[1000px] -translate-x-1/2 rounded-[20px] border-[1px] border-solid border-[#cccc] bg-[#fff] p-[20px]
          shadow-[0px_2px_6px_0px_rgba(81,103,158,0.2)] ${className}`}
          data-spm="xcp"
        >
          <header className="flex items-baseline justify-between">
            <h1 className="bold pb-[10px] text-[16px] text-[#000]">对比栏</h1>
            <CloseOutlined className="cursor-pointer" onClick={onCancel} />
          </header>

          <ul className="flex">
            {compareList?.map((compareItem, idx) => {
              return (
                <li key={idx} className="mt-[10px]">
                  {compareItem?.productId === '-1' ? defaultItem(idx) : productItem(compareItem, idx)}
                </li>
              );
            })}
            <li className="block w-[100px] border-l-[1px] border-solid border-[#ccc] p-[10px] text-center">
              <button
                ref={compareBtnRef}
                className="h-[25px] w-[60px] rounded bg-[#0093E6] text-[#fff]"
                onClick={compareProduct}
                data-spm="cmp"
              >
                对比
              </button>
              <button className="mt-[10px] text-[#0093e6]" onClick={deleteAllProducts}>
                清空对比栏
              </button>
            </li>
          </ul>
        </div>
      )}
    </>
  );
}
